import { SavedInvestigationLines } from './ClinicalInvestigations';
import type { ClinicalInvestigationsView } from '@/dashboard/api';
/**
 * EstimatePrintView -- the printable TREATMENT ESTIMATE
 * (DOCTOR-CONSULT-DELTA-V1; tracker row "Print estimate", doctor sheet,
 * cost-estimation section).
 *
 * The ESTIMATE member of the existing print family beside BillPrintView (the
 * itemized appointment bill) and ReceiptPrintView (the committed payment
 * receipt), rendered through the SAME shared PrintSheet rail: the #442
 * chrome-free swap, then window.print() on the browser's default stylesheet.
 *
 * THE PRINT-NEVER-REPEATS-MONEY LAW (R-G / FS-29): this view renders an
 * ALREADY-READ plan; it fires no fetch and no write, so a print can never
 * repeat, alter or create a money record.
 *
 * V14g receives a separately verified saved snapshot and the same estimate
 * projection used by Plan. Whole-INR quote rows and minor-unit fee/discount
 * facts retain their existing formatters. Missing money facts keep net totals
 * unavailable. The legacy optional-prop consumer retains its old read model.
 * This component never fetches, saves a clinical record or writes money.
 */

import PrintSheet from '@/dashboard/ui/PrintSheet';
import { savedToothRefLabel } from './tooth-diagram-view';
import { receiptIstStamp } from '@/dashboard/billing/ReceiptPrintView';
import { planItemStatusLabel } from '@/dashboard/clinical-workbench/case-sheet-labels';
import { outcomeReasonLabel } from '@/dashboard/owner/Lead360Panel';
import {
  planItemPriceDisplay,
  planTotalDisplay,
  pricingUnitLabel,
} from '@/dashboard/doctor/treatment-picker-view';
import { acceptedEstimateTotal } from '@/dashboard/doctor/consult-close';
import {
  rupeeGlyphToRs,
  rupeesLabel,
} from '@/dashboard/doctor/doctor-consult-view';
import { consultPlanEstimate, rupeesFromMinorLabel } from './doctor-consult-view';
import type { ClinicalPlanItemView, CaseSheetClinicalView } from '@/dashboard/api';

/** The accepted-total cell copy: the existing >0 accepted derivation, or the
 *  honest absence (never "Rs 0 accepted" -- the F2 finding, one vocabulary
 *  on every surface). */
export function estimateAcceptedLine(sheet: CaseSheetClinicalView): string {
  const accepted = acceptedEstimateTotal(sheet.planItems);
  return accepted === null
    ? 'No accepted treatment with a recorded amount'
    : rupeesLabel(accepted);
}

/** The plan-total cell copy: THE ONE plan-total derivation (which already
 *  says plainly when unpriced lines are excluded), re-skinned to the doctor
 *  money grammar. The derivation RETURNS a sentence that opens with its own
 *  label, so -- exactly as the consult rollup's PLAN cell does -- the cell
 *  strips the label words its own <dt> already prints and the trailing stop.
 *  Zero digit math: the amount is the derivation's, byte for byte. An empty
 *  plan has no total to state. */
export function estimateTotalLine(sheet: CaseSheetClinicalView): string {
  const total = planTotalDisplay(sheet.planItems);
  if (total === '') return 'No procedures recorded';
  return rupeeGlyphToRs(
    total.replace(/^Plan total:? /, '').replace(/\.\s*$/, ''),
  );
}

/** One printed line's decision cell: the status word, plus the controlled
 *  decline reason when the line was declined (the same pairing the ledger
 *  row renders on screen). */
export function estimateLineDecision(
  item: CaseSheetClinicalView['planItems'][number],
): string {
  const status = planItemStatusLabel(item.status);
  return item.status === 'declined' && item.declineReason !== null
    ? `${status} (${outcomeReasonLabel(item.declineReason)})`
    : status;
}

export interface EstimatePrintViewProps {
  readonly sheet: CaseSheetClinicalView;
  /** Independent saved reads after joining drafts; source absence stays unknown. */
  readonly savedEstimate?: {
    readonly investigations?: ClinicalInvestigationsView | null;
    readonly consultationFee?: number | null;
    readonly consultationFeeReason?: string | null;
    readonly rows: readonly (Pick<ClinicalPlanItemView,'id'|'procedureName'|'toothArea'|'toothRefs'|'estimatedValue'|'status'|'completedAt'|'completedInCaseSheetId'> & {owningSheetId:number;doctorLabel:string|null})[];
    readonly totals: ReturnType<typeof consultPlanEstimate>;
  };
  /** Same patient-history ordinal as the masthead; never the case-sheet ID. */
  readonly visitOrdinal?: number | null;
  /** The clinic display label (caller-resolved from its own lawful wire). */
  readonly clinicLabel: string;
  /** The patient's display name, or null when the caller holds none. */
  readonly patientLabel: string | null;
  readonly patientUmr?: string|null;
  readonly backLabel: string;
  readonly onBack: () => void;
}

export default function EstimatePrintView({
  sheet,
  savedEstimate,
  visitOrdinal = null,
  clinicLabel,
  patientLabel,
  patientUmr,
  backLabel,
  onBack,
}: EstimatePrintViewProps): JSX.Element {
  // LES: never an array method on a value that can be undefined -- the wire
  // type makes planItems required, and this local keeps that true for every
  // fixture shape that reaches the component.
  const items = sheet.planItems ?? [];
  return (
    <PrintSheet
      title="Treatment estimate"
      subs={[
        clinicLabel,
        `${patientLabel ?? 'Patient record'}${visitOrdinal !== null ? ` - Visit ${visitOrdinal}` : ''} - Appointment #${sheet.appointmentId}`,
        `Generated ${receiptIstStamp(new Date().toISOString())}`,
      ]}
      backLabel={backLabel}
      onBack={onBack}
      testId="estimate-print"
    >
      {savedEstimate ? <article className="consult-v14g__estimate-paper">
       <header><strong>{clinicLabel}</strong><span>Treatment estimate</span></header>
       <h2>Treatment estimate</h2><p className="consult-v14g__paper-meta">{patientLabel??'Patient record'} · {patientUmr??'UMR unavailable'} · {sheet.status==='finalized'?'Finalized':'Saved draft'} · Updated {receiptIstStamp(sheet.updatedAt)}</p>
       <SavedEstimateBody value={savedEstimate}/>
       <p className="consult-v14g__paper-meta">An estimate, not a bill. Payments are taken at Reception.</p>
      </article> : <>
      <div className="ac-table-scroll">
        <table className="table ac-table" aria-label="Treatment estimate">
          <thead>
            <tr>
              <th scope="col">Treatment</th>
              <th scope="col">Tooth / area</th>
              <th scope="col">Decision</th>
              <th scope="col" className="ac-table__cell--end">
                Estimate
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} data-testid="estimate-print-empty">
                  No procedures recorded for this visit.
                </td>
              </tr>
            ) : null}
            {items.map((item) => {
              const unit = pricingUnitLabel(item.catalogPricingUnit ?? null);
              return (
                <tr key={item.id} data-testid={`estimate-print-line-${item.id}`}>
                  <td>{item.procedureName}</td>
                  <td>
                    {item.toothArea !== null && item.toothArea !== ''
                      ? item.toothArea
                      : '-'}
                  </td>
                  <td>{estimateLineDecision(item)}</td>
                  <td className="ac-table__cell--end">
                    {rupeeGlyphToRs(planItemPriceDisplay(item.estimatedValue))}
                    {unit !== '' ? ` (${unit})` : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <dl className="billing-entry__receipt-facts" data-testid="estimate-print-totals">
        <div>
          <dt>Plan total</dt>
          <dd data-testid="estimate-print-total">{estimateTotalLine(sheet)}</dd>
        </div>
        <div>
          <dt>Accepted</dt>
          <dd data-testid="estimate-print-accepted">
            {estimateAcceptedLine(sheet)}
          </dd>
        </div>
      </dl></>}
      {!savedEstimate ? <p className="dashboard-page__note" data-testid="estimate-print-note">
        This is the treatment plan recorded at this visit, with the amounts
        estimated for it. It is not a bill or a payment receipt.
        View the itemised bill and saved payment receipts separately.
      </p> : null}
    </PrintSheet>
  );
}


function SavedEstimateBody({value}:{value:NonNullable<EstimatePrintViewProps['savedEstimate']>}){
 const rows=value.rows.filter(item=>item.status!=='declined'&&item.status!=='deferred').sort((a,b)=>Number(b.status==='accepted')-Number(a.status==='accepted'));
 const omitted=value.rows.filter(item=>item.status==='declined'||item.status==='deferred');
 const total=value.totals;
 return <>
  <div className="ac-table-scroll"><table className="table ac-table" aria-label="Treatment estimate"><thead><tr><th scope="col">Tooth / area</th><th scope="col">Treatment</th><th scope="col">Doctor</th><th scope="col">Answer</th><th scope="col">Estimate</th></tr></thead><tbody>
   {rows.map(item=><tr key={`${item.owningSheetId}:${item.id}`} data-testid={`estimate-print-line-${item.id}`}><td>{item.toothRefs?.length?item.toothRefs.map(ref=>savedToothRefLabel(ref,'fdi')).join(', '):item.toothArea||'Not recorded'}</td><td>{item.procedureName}</td><td>{item.doctorLabel??'Name unavailable'}</td><td>{item.status==='advised'?'Advised — not yet agreed':planItemStatusLabel(item.status)}</td><td>{item.estimatedValue===null?'Price not set':rupeesLabel(item.estimatedValue)}</td></tr>)}
   {!rows.length?<tr><td colSpan={5} data-testid="estimate-print-empty">No proposed treatment recorded.</td></tr>:null}
  </tbody></table></div>
  <p>Consultation: {value.consultationFee == null ? 'Unavailable' : rupeesFromMinorLabel(value.consultationFee)}{value.consultationFeeReason ? ` · ${value.consultationFeeReason}` : ''}</p>
  <SavedInvestigationLines value={value.investigations}/>
  <SavedEstimateTotals totals={total}/>
  {omitted.length?<section><h3>Not included in this estimate</h3><ul>{omitted.map(item=><li key={`${item.owningSheetId}:${item.id}`}>{item.procedureName} · {planItemStatusLabel(item.status)}</li>)}</ul></section>:null}
 </>;
}

/** Shared by all three saved outputs; never a second money calculation. */
export type SavedEstimateSnapshot = NonNullable<EstimatePrintViewProps['savedEstimate']>;
export function SavedEstimateTotals({totals:total}:{totals:SavedEstimateSnapshot['totals']}) {
 const money=(minor:number|null)=>minor===null?'Unavailable':rupeesFromMinorLabel(minor);
 return <section aria-label="Estimate breakdown">
  <p className="consult-v14g__paper-agreed"><b>{total.agreed===null?'Patient agreed: unavailable':`Patient has agreed to ${money(total.agreed)}`}</b></p>
  <p>Today’s charges {money(total.today)} · Accepted treatment before extra discount {money(total.accepted.minor)} · Accepted treatment total {money(total.acceptedNet)}</p>
  {total.advised.count?<p>Advised · not yet agreed {money(total.advised.minor)}</p>:null}
  <dl className="consult-v14g__paper-totals" data-testid="estimate-print-totals">{[
   ['Treatments before extra discount',total.gross],['Extra discount on accepted treatments',total.extra],
   ['Treatment estimate',total.estimate],['Today’s charges',total.today],['Total estimate',total.total],
  ].map(([label,amount])=><div key={String(label)}><dt>{label}</dt><dd>{money(amount as number|null)}</dd></div>)}</dl>
  <p className="consult-v14g__paper-meta">An estimate, not an amount due today. Payments and receipts at Reception.</p>
  {total.extra===null?<p>Extra discount is unavailable. Net estimates cannot be confirmed.</p>:null}
 </section>;
}
